import { axiosService } from "@picovico/helpers";
import { defaultTheme } from "@picovico/ui";
import {
  getLocalStorageData,
  isSSR,
  removeLocalStorageData,
  storeLocalStorageData,
} from "@picovico/utils";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import apiRoutes from "routes/apiRoutes";
import { WebConfig } from "types/index";

type configSliceInterface = {
  configInitialized: boolean;
  configLoadFail: boolean;
};

const initialState: WebConfig & configSliceInterface = {
  configInitialized: false,
  company_idx: "",
  cinema_halls: [],
  configLoadFail: false,
  title: "Movie Hall",
  idx: "no_idx",
  pages: null,
  template: null,
  template_props: null,
  site_theme: defaultTheme,
  loyalty_config: null,
  food_config: null,
  tenant_domain: "",
};

export const loadConfigAsync = createAsyncThunk(
  "config/load",
  async (state, thunkAPI) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const store = thunkAPI.getState() as any;
    const config = store.config;

    if (config?.idx !== "no_idx") return config;
    try {
      const url = apiRoutes["config"];
      const fetchConfig = () => axiosService({ url, method: "GET" });
      let data;
      try {
        ({ data } = await fetchConfig());
      } catch (err) {
        // The interceptor attaches the stored token to every request, and
        // the backend rejects a stale one even on this public endpoint.
        // Drop it and retry as a guest instead of showing the 404 page.
        if (isSSR() || !getLocalStorageData("token")) throw err;
        removeLocalStorageData("token");
        ({ data } = await fetchConfig());
      }
      if (!isSSR()) {
        storeLocalStorageData("companyIdx", data.data?.company_idx);
        storeLocalStorageData("tDom", data.data?.tenant_domain);
      }
      return { ...data.data };
    } catch (err) {
      console.error("loadConfigAsyncError", err);
      if (!isSSR()) localStorage.clear();
      return thunkAPI.rejectWithValue(err || "Failed to load config");
    }
  },
);

export const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    updateConfig(state: WebConfig & configSliceInterface, action) {
      return {
        ...state,
        ...action.payload,
        configInitialized: true,
        configLoadFail: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadConfigAsync.pending, (state) => {
        return {
          ...state,
          configInitialized: false,
          configLoadFail: false,
        };
      })
      .addCase(loadConfigAsync.fulfilled, (state, action) => {
        return {
          ...state,
          ...action.payload,
          configLoadFail: false,
          configInitialized: true,
        };
      })
      .addCase(loadConfigAsync.rejected, (state) => {
        return {
          ...state,
          configLoadFail: true,
          configInitialized: true,
        };
      });

    builder.addCase(HYDRATE, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { payload } = action as any;
      const config = payload?.config ?? {};
      return {
        ...state,
        ...config,
      };
    });
  },
});

export const { updateConfig } = configSlice.actions;
